/**
 * Hook for refreshing source URLs
 *
 * Provides functions to refresh expired or expiring SAS URLs
 * for source documents, videos, and audio files.
 */

import { useState, useCallback, useEffect } from 'react'
import { getSourceService, type RefreshedUrl } from '@/services/sourceService'
import { useRagProxyBase } from '@/hooks/useRagProxyBase'
import type { DisplaySource } from '@/types/source'

interface UseSourceUrlReturn {
  /** Refresh URL for a single source */
  refreshUrl: (source: DisplaySource) => Promise<DisplaySource | null>
  /** Refresh URLs for multiple sources */
  refreshUrls: (sources: DisplaySource[]) => Promise<DisplaySource[]>
  /** Refresh only expired or expiring sources */
  refreshExpiringSources: (
    sources: DisplaySource[],
    bufferMinutes?: number
  ) => Promise<DisplaySource[]>
  /** Whether a refresh is in progress */
  isRefreshing: boolean
  /** Error message if refresh failed */
  error: string | null
  /** Clear the error state */
  clearError: () => void
}

/**
 * Hook for managing source URL refresh
 */
export function useSourceUrl(): UseSourceUrlReturn {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Get the deployment-level RAG base URL to configure the service.
  // Was connections.ragServerUrl pre-Phase-5; now reads from the
  // featureStore via useRagProxyBase().
  const ragProxyBase = useRagProxyBase()

  useEffect(() => {
    const service = getSourceService()
    service.setBaseUrl(ragProxyBase)
  }, [ragProxyBase])

  /**
   * Clear the error state
   */
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  /**
   * Refresh URL for a single source
   */
  const refreshUrl = useCallback(
    async (source: DisplaySource): Promise<DisplaySource | null> => {
      setIsRefreshing(true)
      setError(null)

      try {
        const service = getSourceService()
        const refreshed = await service.refreshSourceUrl(source.file_id)

        // Return updated source with new URL info
        return {
          ...source,
          storage_url: refreshed.storage_url,
          storage_url_type: refreshed.storage_url_type as DisplaySource['storage_url_type'],
          storage_url_expires_at: refreshed.storage_url_expires_at,
          isExpired: false,
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to refresh URL'
        setError(message)
        console.error('[useSourceUrl] Failed to refresh URL:', err)
        return null
      } finally {
        setIsRefreshing(false)
      }
    },
    []
  )

  /**
   * Refresh URLs for multiple sources
   */
  const refreshUrls = useCallback(
    async (sources: DisplaySource[]): Promise<DisplaySource[]> => {
      if (sources.length === 0) {
        return sources
      }

      setIsRefreshing(true)
      setError(null)

      try {
        const service = getSourceService()
        const fileIds = sources.map((s) => s.file_id)
        const refreshed = await service.refreshSourceUrls(fileIds)

        // Create a map for quick lookup
        const urlMap = new Map<string, RefreshedUrl>(
          refreshed.map((r) => [r.file_id, r])
        )

        // Update sources with refreshed URLs
        return sources.map((source) => {
          const updated = urlMap.get(source.file_id)
          if (updated) {
            return {
              ...source,
              storage_url: updated.storage_url,
              storage_url_type: updated.storage_url_type as DisplaySource['storage_url_type'],
              storage_url_expires_at: updated.storage_url_expires_at,
              isExpired: false,
            }
          }
          return source
        })
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to refresh URLs'
        setError(message)
        console.error('[useSourceUrl] Failed to refresh URLs:', err)
        return sources
      } finally {
        setIsRefreshing(false)
      }
    },
    []
  )

  /**
   * Refresh only sources that are expired or expiring soon
   */
  const refreshExpiringSources = useCallback(
    async (
      sources: DisplaySource[],
      bufferMinutes: number = 5
    ): Promise<DisplaySource[]> => {
      const service = getSourceService()

      // Filter to only sources that need refresh
      const needsRefresh = sources.filter(
        (s) =>
          s.isExpired ||
          service.isUrlExpiringSoon(s.storage_url_expires_at, bufferMinutes)
      )

      if (needsRefresh.length === 0) {
        return sources
      }

      setIsRefreshing(true)
      setError(null)

      try {
        const fileIds = needsRefresh.map((s) => s.file_id)
        const refreshed = await service.refreshSourceUrls(fileIds)

        // Create a map for quick lookup
        const urlMap = new Map<string, RefreshedUrl>(
          refreshed.map((r) => [r.file_id, r])
        )

        // Update sources with refreshed URLs
        return sources.map((source) => {
          const updated = urlMap.get(source.file_id)
          if (updated) {
            return {
              ...source,
              storage_url: updated.storage_url,
              storage_url_type: updated.storage_url_type as DisplaySource['storage_url_type'],
              storage_url_expires_at: updated.storage_url_expires_at,
              isExpired: false,
            }
          }
          return source
        })
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to refresh URLs'
        setError(message)
        console.error('[useSourceUrl] Failed to refresh expiring URLs:', err)
        return sources
      } finally {
        setIsRefreshing(false)
      }
    },
    []
  )

  return {
    refreshUrl,
    refreshUrls,
    refreshExpiringSources,
    isRefreshing,
    error,
    clearError,
  }
}
